import { formatDuration, intervalToDuration } from "date-fns";
import JSZip from "jszip";
import { IAppConfig, IAppTemplate } from "models/App";
import { IAuthorization } from "models/Authorization";
import {
  ActionButtonMessageType,
  ActionType,
  AuthorizationRequiredFields,
  AuthorizationStatusType,
  CopyMessageType,
  Country,
  DocumentRejectReason,
  DocumentRejectReasonMessage,
  FormLocationNextStep,
  IdentityDocumentStatus,
  IdentityRejectReason,
  IdentityRejectReasonMessage,
  IdentityRequirement,
  IdentityTransactionStatus,
  IdentityVerificationSimilarity,
  IdentityVerificationStatus,
  PatientStatus,
  ProtectedRouteType,
  RequiredDemographics,
} from "models/Constants";
import { EmbeddedSessionLocationEnum } from "models/Form";
import {
  IIdentityResult,
  IIdentityValidation,
} from "models/IdentityValidation";
import { IProviderAddress, IProviderInfo } from "models/ProviderSearch";
import { IAddress, IRootLife } from "models/RootLife";
import { ISession, ISessionMetadata } from "models/Session";
import { ISignature } from "models/Signature";
import { fhirResourceIconDictionary } from "./Collections";
import { authNamePrefix } from "./Regex";

export interface IAddressLabel {
  stateProvince: string;
  zipPostalMask: string;
  zipPostalPlaceHolder: string;
}

export const getAddressLabel = (country: string, postalCode: string = "") => {
  const isUSA = country === Country.UnitedStates;
  return {
    stateProvince: isUSA ? "State" : "Province",
    zipPostalMask: isUSA
      ? unmask(postalCode).length < 6
        ? "999999"
        : "99999-9999"
      : "a9a 9a9",
    zipPostalPlaceHolder: isUSA ? "Zip Code" : "Postal Code",
  };
};

export const requiresProfileReview = (
  rootLife?: IRootLife,
  authorizations?: IAuthorization[]
) => {
  if (!rootLife) {
    return true;
  }

  const authRequiredFields = getAuthorizationsRequiredFields(
    authorizations ?? []
  );

  const missingAuthRequiredFields = authRequiredFields.filter((field) =>
    isAuthFieldMissing(rootLife, field)
  );

  return (
    !rootLife.isProfileReviewed ||
    !rootLife.addresses ||
    missingAuthRequiredFields.length > 0
  );
};

export const getAuthorizationsRequiredFields = (
  authorizations: IAuthorization[]
) => {
  return [
    ...new Set(
      authorizations
        .filter((a) => a.requiredFields?.length > 0)
        .map((a) => a.requiredFields)
        .flat()
    ),
  ];
};

export const isAuthFieldMissing = (
  rootLife: IRootLife,
  field: string
): boolean => {
  const lifePrefix = "Life";

  const fieldParts = field.split(".");
  const fieldPrefix = fieldParts[0];

  if (fieldPrefix === lifePrefix) {
    const value = getFieldValueFromRootLife(
      rootLife,
      field.substring(field.indexOf(".") + 1)
    );

    return !value?.trim();
  }

  return false;
};

export const getFieldValueFromRootLife = (
  rootLife: IRootLife,
  field: string
): string | undefined => {
  const fieldParts = field.split(".");

  const fieldName = Object.keys(rootLife).find(
    (f) => f.toLowerCase() === fieldParts[0].toLowerCase()
  );

  if (!fieldName) {
    return undefined;
  }

  const customFieldsValuesProvider: {
    [key: string]: (subfield: string) => string | undefined;
  } = {
    addresses: (subfield: string) => {
      const primaryAddress = rootLife.addresses?.find(
        (a) => a.isPrimary === true
      );
      if (!primaryAddress) {
        return undefined;
      }
      const subFieldName = Object.keys(primaryAddress).find(
        (f) => f.toLowerCase() === subfield.toLowerCase()
      );

      if (!subFieldName) {
        return undefined;
      }

      return primaryAddress?.[subFieldName as keyof IAddress]?.toString();
    },
    identifiers: (subfield: string) => {
      const identifier = rootLife.identifiers?.find((a) => a.type === subfield);
      return identifier?.value;
    },
  };

  if (customFieldsValuesProvider[fieldName]) {
    const valueProviderFunc = customFieldsValuesProvider[fieldName];
    const subfield = fieldParts[1];

    return valueProviderFunc(subfield);
  }

  return rootLife[fieldName as keyof IRootLife]?.toString();
};

export const getProfileRequiredFieldsCriterias = () => {
  return {
    deceasedDate: (
      patientStatus?: string,
      authorizationsRequiredFields?: ReadonlyArray<string>
    ) =>
      patientStatus === PatientStatus.Deceased ||
      authorizationsRequiredFields?.includes(
        AuthorizationRequiredFields.DeceasedDate
      ),
    gender: (authorizationsRequiredFields?: ReadonlyArray<string>) =>
      authorizationsRequiredFields?.includes(
        AuthorizationRequiredFields.Gender
      ),
    country: (
      isAddressMandatory?: boolean,
      mandatoryDemographics?: ReadonlyArray<string>,
      reasons?: ReadonlyArray<string>,
      authorizationsRequiredFields?: ReadonlyArray<string>
    ) =>
      isAddressMandatory ||
      !!mandatoryDemographics?.some(
        (m) =>
          m === RequiredDemographics.Address ||
          m === RequiredDemographics.County
      ) ||
      reasons?.some((reason) =>
        reason.includes(RequiredDemographics.Address)
      ) ||
      authorizationsRequiredFields?.includes(
        AuthorizationRequiredFields.Address_Country
      ),
    addressLine1: (
      isAddressMandatory: boolean,
      mandatoryDemographics?: ReadonlyArray<string>,
      reasons?: ReadonlyArray<string>,
      authorizationsRequiredFields?: ReadonlyArray<string>
    ) =>
      isAddressMandatory ||
      !!mandatoryDemographics?.some(
        (m) =>
          m === RequiredDemographics.Address ||
          m === RequiredDemographics.County
      ) ||
      reasons?.some((reason) =>
        reason.includes(RequiredDemographics.Address)
      ) ||
      authorizationsRequiredFields?.includes(
        AuthorizationRequiredFields.Address_AddressLine1
      ),
    addressLine2: (authorizationsRequiredFields?: ReadonlyArray<string>) =>
      authorizationsRequiredFields?.includes(
        AuthorizationRequiredFields.Address_AddressLine2
      ),
    city: (
      isAddressMandatory: boolean,
      mandatoryDemographics?: ReadonlyArray<string>,
      reasons?: ReadonlyArray<string>,
      authorizationsRequiredFields?: ReadonlyArray<string>
    ) =>
      isAddressMandatory ||
      !!mandatoryDemographics?.some(
        (m) =>
          m === RequiredDemographics.Address ||
          m === RequiredDemographics.County
      ) ||
      reasons?.some((reason) =>
        reason.includes(RequiredDemographics.Address)
      ) ||
      authorizationsRequiredFields?.includes(
        AuthorizationRequiredFields.Address_City
      ),
    state: (
      isAddressMandatory: boolean,
      mandatoryDemographics?: ReadonlyArray<string>,
      reasons?: ReadonlyArray<string>,
      authorizationsRequiredFields?: ReadonlyArray<string>
    ) =>
      isAddressMandatory ||
      !!mandatoryDemographics?.some(
        (m) =>
          m === RequiredDemographics.Address ||
          m === RequiredDemographics.County
      ) ||
      reasons?.some((reason) =>
        reason.includes(RequiredDemographics.Address)
      ) ||
      authorizationsRequiredFields?.includes(
        AuthorizationRequiredFields.Address_State
      ),
    postalCode: (
      isAddressMandatory: boolean,
      mandatoryDemographics?: ReadonlyArray<string>,
      reasons?: ReadonlyArray<string>,
      authorizationsRequiredFields?: ReadonlyArray<string>
    ) =>
      isAddressMandatory ||
      !!mandatoryDemographics?.some(
        (m) =>
          m === RequiredDemographics.Address ||
          m === RequiredDemographics.County
      ) ||
      reasons?.some((reason) =>
        reason.includes(RequiredDemographics.Address)
      ) ||
      authorizationsRequiredFields?.includes(
        AuthorizationRequiredFields.Address_PostalCode
      ),
    county: (
      mandatoryDemographics?: ReadonlyArray<string>,
      authorizationsRequiredFields?: ReadonlyArray<string>
    ) =>
      mandatoryDemographics?.includes(RequiredDemographics.County) &&
      authorizationsRequiredFields?.includes(
        AuthorizationRequiredFields.Address_County
      ),
    ssn: (
      mandatoryDemographics?: ReadonlyArray<string>,
      reasons?: ReadonlyArray<string>,
      authorizationsRequiredFields?: ReadonlyArray<string>
    ) =>
      !!mandatoryDemographics?.includes(RequiredDemographics.SSN) ||
      reasons?.some((reason) => reason.includes(RequiredDemographics.SSN)) ||
      authorizationsRequiredFields?.includes(AuthorizationRequiredFields.SSN),

    phone: (
      mandatoryDemographics?: ReadonlyArray<string>,
      reasons?: ReadonlyArray<string>
    ) =>
      !!mandatoryDemographics?.includes(RequiredDemographics.PhoneNumber) ||
      reasons?.some((reason) =>
        reason.includes(RequiredDemographics.PhoneNumber)
      ),
  };
};

/**
 * Function that takes form input as a string and restructure it based on the input format type
 * @param {string} value string
 * @param {string} type "ssn" | "postalCode"
 * @returns {string} value of the input field that matches the input format
 * @example <caption> Example usage value: 111111111 type: postalCode</caption>
 * // returns 11111-1111.
 */
export const mask = (
  value: string | undefined,
  type: "ssn" | "postalCode"
): string => {
  if (!value) {
    return "";
  }
  switch (type) {
    case "ssn":
      return `${value.substring(0, 3)}-${value.substring(
        3,
        5
      )}-${value.substring(5)}`;
    case "postalCode":
      return value.length === 6
        ? `${value.substring(0, 3)} ${value.substring(3)}`
        : value.length >= 6
        ? `${value.substring(0, 5)}-${value.substring(5)}`
        : value;
    default:
      return value;
  }
};
export const unmask = (value: string) =>
  value.replace(/[^a-zA-Z0-9]|\s|_|\/|-/g, "");

export const maskPhone = (phone: string) => {
  const formattedPhone =
    phone.slice(0, 3) + "-" + phone.slice(3, 6) + "-" + phone.slice(6);
  return (
    formattedPhone.slice(0, 9).replace(/\d/g, "*") + formattedPhone.slice(9)
  );
};

export const parseJwt = (accessToken: string) => {
  var base64Url = accessToken.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

export const getRandomId = () => {
  return "_" + Math.random().toString(36).substr(2, 9);
};

export const getAuthsCopyMessage = (
  authorizations: Array<IAuthorization>,
  appName: string
) => {
  if (
    authorizations.every((e) =>
      e.name.startsWith(ProtectedRouteType.Retrievals)
    )
  ) {
    if (
      authorizations.every(
        (e) => e.authorizeUri == null && !authorizations.every((e) => e.isPdf)
      )
    ) {
      return CopyMessageType.Retrieval.replace(`\${appName}`, appName);
    } else if (authorizations.every((e) => e.authorizeUri !== null)) {
      return CopyMessageType.PatientPortal.replace(`\${appName}`, appName);
    } else if (authorizations.every((e) => e.isPdf)) {
      return CopyMessageType.ESignature.replace(`\${appName}`, appName);
    }
  }
};

export const getAuthCopyMessage = (
  authorization: IAuthorization,
  appName: string
) => {
  if (
    authorization.name.startsWith(ProtectedRouteType.Data) ||
    authorization.name.startsWith(ProtectedRouteType.Lives)
  )
    return CopyMessageType.MyData.replace(`\${appName}`, appName);

  if (
    authorization.name.startsWith(ProtectedRouteType.Retrievals) &&
    authorization.authorizeUri === null &&
    !authorization.isPdf
  ) {
    return CopyMessageType.Retrieval.replace(`\${appName}`, appName);
  }

  if (authorization.authorizeUri !== null)
    return CopyMessageType.PatientPortal.replace(`\${appName}`, appName);

  if (authorization.isPdf) {
    return CopyMessageType.ESignature.replace(`\${appName}`, appName);
  }
};

export const getSessionsButtonsText = (
  authorization: IAuthorization,
  signatures?: Array<ISignature>,
  pdf?: File | Blob
) => {
  if (authorization.authorizeUri != null) {
    return {
      secondaryButtonText: ActionButtonMessageType.AuthorizePatientPortal,
      additionalSecondaryButtonText: ActionButtonMessageType.Deny,
    };
  } else if (signatures && signatures.length > 0) {
    if (pdf) {
      return {
        secondaryButtonText: ActionButtonMessageType.Authorize,
        additionalSecondaryButtonText: ActionButtonMessageType.Deny,
      };
    } else
      return {
        secondaryButtonText: ActionButtonMessageType.ReviewPdfAuthorization,
        additionalSecondaryButtonText: ActionButtonMessageType.Deny,
      };
  } else if (!!signatures && authorization.isPdf) {
    return {
      secondaryButtonText: ActionButtonMessageType.SignHere,
      additionalSecondaryButtonText: ActionButtonMessageType.Deny,
    };
  } else
    return {
      secondaryButtonText: ActionButtonMessageType.Authorize,
      additionalSecondaryButtonText: ActionButtonMessageType.Deny,
    };
};

export const getSessionMetadata = () => {
  const sessionMetadataItem = localStorage.getItem("sessionMetadata");
  if (sessionMetadataItem) {
    return JSON.parse(sessionMetadataItem) as ISessionMetadata;
  }
};

export const saveSessionMetadata = (
  sessionId: string,
  sessionKey: string,
  lifeId: string,
  oboId?: string
) => {
  localStorage.setItem(
    "sessionMetadata",
    JSON.stringify({ sessionId, sessionKey, lifeId, oboId })
  );
};
export const saveSessionOBOIdToMetadata = (oboId: string) => {
  const sessionMetadataItem = localStorage.getItem("sessionMetadata");
  if (sessionMetadataItem) {
    const sessionMetadata = JSON.parse(sessionMetadataItem) as ISessionMetadata;

    saveSessionMetadata(
      sessionMetadata.sessionId,
      sessionMetadata.sessionKey,
      sessionMetadata.lifeId,
      oboId
    );
  }
};

export const clearSessionMetadata = () => {
  localStorage.removeItem("sessionMetadata");
};

/**
 * Function that takes authorization name and return the prefix
 * @param {string} name
 * @returns {string} prefix if matches and empty string if not
 * @example <caption> Example usage name: ~/lives/observations.read </caption>
 * // returns ~/lives/.
 */
export const extractAuthNamePrefix = (name: string) => {
  return `${name.match(authNamePrefix)}`;
};

export const replaceAppNameInAppTemplate = (
  template: IAppTemplate,
  appName: string
): IAppTemplate => {
  const stringified = JSON.stringify(template);
  return JSON.parse(stringified.replaceAll(/<App Name>/gi, appName));
};

export const getIconNameByResource = (resource: string) => {
  return `/images/${
    fhirResourceIconDictionary[resource] ?? "DefaultFhirResourceIcon"
  }.svg`;
};

/**
 * Masks an email by hiding some characters of it and replacing them with *
 * @param email
 */
export const maskEmail = (email: string | undefined): string | undefined => {
  if (email) {
    const indexOfLastMaskedChar = email.indexOf("@") - 3;
    if (indexOfLastMaskedChar <= 0) {
      return email;
    } else {
      var maskedPart = "*****";
      if (email.indexOf("@") < 8) {
        maskedPart = new Array(indexOfLastMaskedChar).fill("*").join("");
      }
      const unmaskedPart = email.substring(indexOfLastMaskedChar);
      return `${maskedPart}${unmaskedPart}`;
    }
  }
};
export const distinct = function <T>(value: T, index: number, self: Array<T>) {
  return self.indexOf(value) === index;
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName.replace(/['"]+/g, "");
  document.body.append(link);
  link.click();
  link.remove();
  window.addEventListener("focus", () => URL.revokeObjectURL(link.href), {
    once: true,
  });
};

export const getUpdatedAuths = () => {
  const updatedAuths = localStorage.getItem("updatedAuths");
  if (updatedAuths) {
    return JSON.parse(updatedAuths) as Array<string>;
  }
  return [];
};

export const saveUpdatedAuths = (authorizations: Array<string>) => {
  localStorage.setItem(
    "updatedAuths",
    JSON.stringify(getUpdatedAuths()?.concat(authorizations))
  );
};

export const clearUpdatedAuths = () => {
  localStorage.removeItem("updatedAuths");
};

export const saveOrUpdateSelectedProvider = (providerId?: string) => {
  const selectedProvidersJson = localStorage.getItem("selectedProviders");
  if (selectedProvidersJson) {
    let selectedProviders = JSON.parse(selectedProvidersJson) as Array<string>;
    selectedProviders.push(`${providerId}`);
    localStorage.setItem(
      "selectedProviders",
      JSON.stringify(selectedProviders)
    );
  } else {
    localStorage.setItem("selectedProviders", JSON.stringify([providerId]));
  }
};

export const getSelectedProviders = () => {
  const selectedProviders = localStorage.getItem("selectedProviders");
  return selectedProviders
    ? (JSON.parse(selectedProviders) as Array<string>)
    : new Array<string>();
};

export const clearSelectedProvidersData = () => {
  localStorage.removeItem("selectedProviders");
};

export const saveProviderSearchRedirect = (redirectUri: string) => {
  localStorage.setItem("providerSearchRedirect", redirectUri);
};

export const getProviderSearchRedirect = () => {
  return localStorage.getItem("providerSearchRedirect");
};

export const clearProviderSearchData = () => {
  localStorage.removeItem("providerSearchRedirect");
};

export const isDesktopDevice = () =>
  !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

export const canInitiateNewIdentityVerificationSession = (
  attempts: Array<IIdentityValidation>,
  appId?: string,
  identityResult?: IIdentityResult
): boolean => {
  const unsuccessfulIdentityResult = !(
    identityResult?.transactionStatus === IdentityTransactionStatus.Success &&
    identityResult?.documentStatus === IdentityDocumentStatus.Approved &&
    identityResult?.verificationStatus === IdentityVerificationStatus.Verified
  );

  const attemptsRelatedToApp = attempts?.filter((a) => a.appId === appId) ?? [];
  const failedAttempts = attemptsRelatedToApp.filter(
    (a) =>
      a.transactionStatus !== IdentityTransactionStatus.Pending &&
      a.transactionStatus !== IdentityTransactionStatus.Created &&
      (a.identityVerificationResult
        ? a.identityVerificationResult !==
          ActionType.IdentityVerificationSimilarityMatchValidityTrue
        : a.identityVerificationSimilarityResult !==
            IdentityVerificationSimilarity.SimilarityMatch &&
          !a.identityVerificationValidityResult)
  );
  const hasFailedAttemptDueToFraud = attemptsRelatedToApp.some(
    (a) =>
      a.identityDocumentResult === ActionType.IdentityDocumentDeniedFraud ||
      a.documentStatus === IdentityDocumentStatus.DeniedFraud
  );

  return (
    unsuccessfulIdentityResult &&
    failedAttempts.length < 2 &&
    !hasFailedAttemptDueToFraud
  );
};

export const canSkipIdentityPage = (
  didFinishLoading: boolean,
  rootLife?: IRootLife,
  session?: ISession,
  appConfig?: IAppConfig,
  authorizations?: IAuthorization[],
  canInitiateAnotherSession?: boolean,
  providerInfo?: IProviderInfo
) => {
  const didContextLoad =
    didFinishLoading && rootLife && authorizations && session;
  const isVerified =
    appConfig?.isTrustedRequester || rootLife?.isIdentityVerified;
  const hasIdentityRequiredAuth =
    authorizations?.length &&
    authorizations.some(
      ({ identityRequirement }) =>
        identityRequirement === IdentityRequirement.IdAndIdentity
    );
  return (
    didContextLoad &&
    (isVerified || !canInitiateAnotherSession || !hasIdentityRequiredAuth) &&
    providerInfo?.providerRetrievalType?.authorization?.identityRequirement !==
      IdentityRequirement.IdAndIdentity
  );
};

export const getRejectReason = (identityResult?: IIdentityResult) => {
  let messageKey;
  if (
    identityResult?.documentStatus === IdentityDocumentStatus.ErrorNotReadableId
  ) {
    if (
      !identityResult?.documentRejectReason?.rejectReasonDetails?.detailsCode
    ) {
      messageKey = Object.keys(DocumentRejectReason).find(
        (reason) =>
          DocumentRejectReason[reason as keyof typeof DocumentRejectReason] ===
          identityResult?.documentRejectReason?.rejectReasonDescription
      );
    } else {
      messageKey = Object.keys(DocumentRejectReason).find(
        (reason) =>
          DocumentRejectReason[reason as keyof typeof DocumentRejectReason] ===
          identityResult?.documentRejectReason?.rejectReasonDetails
            ?.detailsDescription
      );
    }
  }
  if (identityResult?.documentStatus === IdentityDocumentStatus.Approved) {
    if (
      identityResult?.identityVerification?.similarity ===
      IdentityRejectReason.SimilarityNoMatch
    ) {
      return IdentityRejectReasonMessage.SimilarityNoMatch;
    }
    messageKey = Object.keys(IdentityRejectReason).find(
      (reason) =>
        IdentityRejectReason[reason as keyof typeof IdentityRejectReason] ===
        identityResult?.identityVerification?.reason
    );

    if (messageKey)
      return IdentityRejectReasonMessage[
        messageKey as keyof typeof IdentityRejectReasonMessage
      ];
  }
  return DocumentRejectReasonMessage[
    `${
      messageKey ?? identityResult?.documentStatus
    }` as keyof typeof DocumentRejectReasonMessage
  ];
};

export const providerAddressBuilder = (address?: IProviderAddress): string => {
  if (!address) return "";
  let completeAddress = address?.line ? `${address?.line}` : "";
  completeAddress += address?.line && address?.city ? "," : "";
  completeAddress += address?.city ? `${address?.city}` : "";
  completeAddress += address?.city && address?.state ? "," : "";
  completeAddress += address?.state ? `${address?.state}` : "";
  completeAddress += address?.state && address?.country ? "," : "";
  completeAddress += address?.country ? `${address?.country}` : "";
  completeAddress += address?.country && address?.postalCode ? "," : "";
  completeAddress += address?.postalCode ? `${address?.postalCode}` : "";
  return completeAddress;
};

export const zipFiles = async (
  files: Array<{ file: File; fileName: string }>
) => {
  let zip = new JSZip();
  files.forEach((f) => zip.file(f.fileName, f.file));
  const blobFile = await zip.generateAsync({ type: "blob" });
  return blobFile;
};

export const getEmbeddedSessionLocationNextStep = (
  location: EmbeddedSessionLocationEnum,
  session: ISession,
  sessionId: string,
  isProofOfRelationshipNeeded: boolean
) => {
  if (
    location === EmbeddedSessionLocationEnum.AfterAuthorizations &&
    session.canUserQueryProviders
  )
    return `/session/${sessionId}/${FormLocationNextStep.AfterAuthorizationsWithProviderSearch}`;
  if (
    location === EmbeddedSessionLocationEnum.AfterProfile &&
    isProofOfRelationshipNeeded
  )
    return `/session/${sessionId}/${FormLocationNextStep.AfterProfileWithSdm}`;
  else return `/session/${sessionId}/${FormLocationNextStep[location]}`;
};

export const isNoActionCallbackUri = (callbackUri?: string): boolean => {
  const voidUri = "https://marbleapi.com/void";
  return callbackUri === voidUri;
};

export const getWordWithArticle = (word: string) => {
  const vowels = "aeiouAEIOU";
  return vowels.includes(word.charAt(0)) ? "an " + word : "a " + word;
};

export const stringifyDuration = (duration: number | null) => {
  if (!duration) {
    return "ongoing";
  }
  const startDate = new Date();
  const durationObject = intervalToDuration({
    start: startDate,
    end: new Date(startDate.getTime() + duration * 60000),
  });
  return formatDuration(durationObject, {
    format: ["years", "months", "weeks", "days", "hours", "minutes"],
    delimiter: " and ",
  });
};

export const isAuthExpired = (auth: IAuthorization) => {
  if (
    auth.status === AuthorizationStatusType.PendingReview ||
    auth.duration === null ||
    (Date.now() - Date.parse(auth.acceptedAt)) / 60000 < auth.duration
  ) {
    return false;
  }
  return true;
};
